import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { IconSpecial } from '../components/ui';
import carouselPersona from '../assets/images/intro-v2/carousel-01-persona.png';
import carouselProfile from '../assets/images/intro-v2/carousel-02-profile.png';
import carouselFeedback from '../assets/images/intro-v2/carousel-03-feedback.png';
import carouselPins from '../assets/images/intro-v2/carousel-04-pins.png';
import carouselInsight from '../assets/images/intro-v2/carousel-05-insight.png';
import carouselResearch from '../assets/images/intro-v2/carousel-06-research.png';
import sectionCycle from '../assets/images/intro-v2/section-intro.png';
import sectionEnterprise from '../assets/images/intro-v2/section-enterprise.png';
import './IntroPageV2.scss';

const HERO_SHOWCASES = [
  {
    id: 'persona',
    group: 'persona',
    title: '누구나 구성하는 우리 서비스의 퍼소나',
    description: '우리 서비스에 맞는 퍼소나를 직접 구성하고,\n실제 사용자처럼 반응하는 AI를 언제든 만날 수 있습니다.',
    image: carouselPersona,
    alt: '다양한 도메인의 AI 퍼소나 카드',
  },
  {
    id: 'profile',
    group: 'persona',
    title: '데이터로 모델링한 상세 프로필',
    description: '도메인 특화 변수를 행동 데이터로 모델링하고,\n그 사람이 실제로 어떻게 판단하고 쓰는지 이해할 수 있습니다.',
    image: carouselProfile,
    alt: '퍼소나 상세 프로필과 행동 특성',
  },
  {
    id: 'feedback',
    group: 'test',
    title: 'AI 퍼소나의 UX 피드백',
    description: '화면별, 퍼소나별 점수와 요약을 한눈에 모아 보고,\n빠르게 결과를 확인할 수 있습니다.',
    image: carouselFeedback,
    alt: '퍼소나 피드백 카드와 점수 요약',
  },
  {
    id: 'pins',
    group: 'test',
    title: '화면 기반 생생한 퍼소나의 반응',
    description: '실제 화면 위 핀과 코멘트를 따라가며,\n긍정·부정 반응이 어디에 모이는지 확인할 수 있습니다.',
    image: carouselPins,
    alt: '화면 핀과 퍼소나 코멘트가 표시된 UX 검증 결과',
  },
  {
    id: 'insight',
    group: 'test',
    title: '개선 방향을 한 장의 보고서로',
    description: '자주 언급된 요소와 화면의 중요한 지점을 고려한,\n개선 방향을 한 장의 보고서로 확인할 수 있습니다.',
    image: carouselInsight,
    alt: 'UX 인사이트 보고서와 핵심 개선 방향',
  },
  {
    id: 'research',
    group: 'researcher',
    title: '대화하듯 설계하는 리서치',
    description: 'pxd의 노하우를 가진 리서치 전문가와 함께,\n대화하며 쉽게 리서치를 설계할 수 있습니다.',
    image: carouselResearch,
    alt: '대화형으로 리서치 계획 후보를 만들고 선택하는 화면',
  },
];

const SHOWCASE_TABS = [
  { id: 'persona', step: '01', label: 'AI Persona' },
  { id: 'test', step: '02', label: 'UX Test' },
  { id: 'researcher', step: '03', label: 'AI Researcher' },
];

const INTRO_FEATURES = [
  { id: 'plan', icon: 'researchPlan', label: '리서치 계획서' },
  { id: 'survey', icon: 'screeningSurvey', label: '스크리닝 설문' },
  { id: 'guideline', icon: 'guideline', label: '진행 가이드라인' },
  { id: 'analysis', icon: 'resultsAnalysis', label: '결과 분석' },
  { id: 'ui', icon: 'participantRecruit', label: '화면 사용성 검증' },
  { id: 'ab', icon: 'surveyAnalysis', label: 'A/B 비교검증' },
  { id: 'interview', icon: 'participantScreener', label: '1:1 AI 인터뷰' },
];

const IntroPageV2 = () => {
  const navigate = useNavigate();
  const { isLoggedIn, sessionChecked } = useAppContext();
  const [activeShowcaseIndex, setActiveShowcaseIndex] = React.useState(0);
  const [showcasePaused, setShowcasePaused] = React.useState(false);
  const mainRef = useRef(null);
  const showcaseDragRef = useRef(null);
  const activeShowcase = HERO_SHOWCASES[activeShowcaseIndex];
  const activeShowcaseGroup = activeShowcase.group;

  useEffect(() => {
    if (!sessionChecked || !isLoggedIn) return;
    navigate('/b2b', { replace: true });
  }, [sessionChecked, isLoggedIn, navigate]);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      {
        root,
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    const nodes = root.querySelectorAll('[data-reveal]');
    nodes.forEach((node) => {
      if (node.classList.contains('is-revealed')) return;
      observer.observe(node);
    });

    // 이미 뷰포트 안인 요소는 바로 등장 (탭 전환 포함)
    requestAnimationFrame(() => {
      const rootRect = root.getBoundingClientRect();
      nodes.forEach((node) => {
        if (node.classList.contains('is-revealed')) return;
        const rect = node.getBoundingClientRect();
        const visible = rect.top < rootRect.bottom - 40 && rect.bottom > rootRect.top + 40;
        if (!visible) return;
        node.classList.add('is-revealed');
        observer.unobserve(node);
      });
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (showcasePaused) return undefined;

    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      setActiveShowcaseIndex((current) => (current + 1) % HERO_SHOWCASES.length);
    };

    const timer = window.setInterval(tick, 5500);
    return () => window.clearInterval(timer);
  }, [showcasePaused, activeShowcaseIndex]);

  const goIndividualLogin = () => {
    if (isLoggedIn) {
      navigate('/b2b');
      return;
    }
    navigate('/individual/login');
  };

  const resolveEnterpriseLoginUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/login';
    }
    if (hostname === 'reopsai.com' || hostname === 'www.reopsai.com') {
      return 'https://b2b.reopsai.com/login';
    }
    return '/login';
  };

  const scrollToNextSection = () => {
    const next = document.getElementById('intro-enterprise');
    next?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goToShowcase = (index) => {
    const length = HERO_SHOWCASES.length;
    setActiveShowcaseIndex((index + length) % length);
  };

  const handleShowcasePointerDown = (event) => {
    showcaseDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
  };

  const handleShowcasePointerMove = (event) => {
    const drag = showcaseDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - drag.startX) > 8 || Math.abs(event.clientY - drag.startY) > 8) {
      drag.moved = true;
    }
  };

  const handleShowcasePointerEnd = (event) => {
    const drag = showcaseDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const swiped = Math.abs(dx) >= 56 && Math.abs(dx) > Math.abs(dy);
    if (swiped) {
      drag.moved = true;
      goToShowcase(activeShowcaseIndex + (dx < 0 ? 1 : -1));
    }
  };

  return (
    <div className="intro-v2">
      <header className="intro-v2-header">
        <div className="intro-v2-header-inner">
          <Link to="/" className="intro-v2-logo" aria-label="ReOpsAI">
            <IconSpecial name="logo" className="intro-v2-logo-img" />
          </Link>
          <nav className="intro-v2-nav">
            <Link to="/individual/login" className="intro-v2-header-link">
              로그인
            </Link>
            <span className="intro-v2-header-divider" aria-hidden="true" />
            <a href={resolveEnterpriseLoginUrl()} className="intro-v2-header-link">
              기업 로그인
            </a>
            <a
              href="/inquiry"
              target="_blank"
              rel="noopener noreferrer"
              className="intro-v2-header-btn intro-v2-header-btn--solid"
            >
              도입문의
            </a>
          </nav>
        </div>
      </header>

      <main className="intro-v2-main" ref={mainRef}>
        <section className="intro-v2-panel intro-v2-hero" id="intro-hero">
          <div className="intro-v2-hero-inner">
            <div className="intro-v2-hero-copy is-revealed" data-reveal data-reveal-delay="0">
              <h1 className="intro-v2-hero-title">리서치 준비부터 AI 퍼소나로 UX 검증까지, 한 번에</h1>
              <p className="intro-v2-hero-desc">
                리서치 전문가 없이 AI 에이전트로 준비하고, AI 퍼소나로 검증합니다.
              </p>
              <div className="intro-v2-hero-actions">
                <button type="button" className="intro-v2-btn intro-v2-btn--primary intro-v2-btn--lg" onClick={goIndividualLogin}>
                  체험하기
                </button>
              </div>
            </div>

            <div className="intro-v2-hero-stage">
              <div className="intro-v2-hero-aside">
                <div className="intro-v2-showcase-steps" role="tablist" aria-label="제품 영역 보기">
                  {SHOWCASE_TABS.map((tab) => {
                    const firstIndex = HERO_SHOWCASES.findIndex((item) => item.group === tab.id);
                    const isActive = activeShowcaseGroup === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={isActive ? 'is-active' : ''}
                        onClick={() => goToShowcase(firstIndex)}
                      >
                        <span>{tab.step}</span>
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
                <div className="intro-v2-showcase-copy">
                  <h2 key={activeShowcase.id}>{activeShowcase.title}</h2>
                  <p key={`${activeShowcase.id}-desc`}>{activeShowcase.description}</p>
                </div>
              </div>

              <div className="intro-v2-showcase" data-reveal data-reveal-delay="1">
                <div className="intro-v2-showcase-viewport">
                  <div
                    className="intro-v2-showcase-stage"
                    tabIndex={0}
                    onPointerDown={handleShowcasePointerDown}
                    onPointerMove={handleShowcasePointerMove}
                    onPointerUp={handleShowcasePointerEnd}
                    onPointerCancel={handleShowcasePointerEnd}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        goToShowcase(activeShowcaseIndex + 1);
                      }
                      if (event.key === 'ArrowLeft') {
                        event.preventDefault();
                        goToShowcase(activeShowcaseIndex - 1);
                      }
                    }}
                  >
                    {HERO_SHOWCASES.map((showcase, index) => {
                      const isActive = index === activeShowcaseIndex;
                      return (
                        <figure
                          key={showcase.id}
                          className={`intro-v2-showcase-shot${isActive ? ' is-active' : ''}`}
                          data-shot={showcase.id}
                          data-offset={isActive ? '0' : 'hidden'}
                          aria-hidden={!isActive}
                        >
                          <img src={showcase.image} alt={isActive ? showcase.alt : ''} draggable="false" />
                        </figure>
                      );
                    })}
                  </div>
                </div>
                <div className="intro-v2-showcase-controls">
                  <div className="intro-v2-showcase-pager">
                    <button
                      type="button"
                      className="intro-v2-showcase-nav intro-v2-showcase-nav--prev"
                      onClick={() => goToShowcase(activeShowcaseIndex - 1)}
                      aria-label="이전 화면 보기"
                    >
                      ‹
                    </button>
                    <div className="intro-v2-showcase-dots" role="tablist" aria-label="화면 페이지">
                      {HERO_SHOWCASES.map((showcase, index) => (
                        <button
                          key={`${showcase.id}-dot`}
                          type="button"
                          role="tab"
                          className={index === activeShowcaseIndex ? 'is-active' : ''}
                          aria-label={`${index + 1}번째 화면`}
                          aria-selected={index === activeShowcaseIndex}
                          onClick={() => goToShowcase(index)}
                        />
                      ))}
                    </div>
                    <div className="intro-v2-showcase-pager-end">
                      <button
                        type="button"
                        className="intro-v2-showcase-nav intro-v2-showcase-nav--next"
                        onClick={() => goToShowcase(activeShowcaseIndex + 1)}
                        aria-label="다음 화면 보기"
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        className={`intro-v2-showcase-pause${showcasePaused ? ' is-paused' : ''}`}
                        onClick={() => setShowcasePaused((paused) => !paused)}
                        aria-label={showcasePaused ? '자동 넘김 재생' : '자동 넘김 일시정지'}
                        aria-pressed={showcasePaused}
                      >
                        <span className="intro-v2-showcase-pause-icon" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="intro-v2-scroll-cue"
            onClick={scrollToNextSection}
            aria-label="다음 섹션으로 스크롤"
          >
            <span className="intro-v2-scroll-cue-icon" aria-hidden="true" />
          </button>
        </section>

        <section className="intro-v2-panel intro-v2-section intro-v2-enterprise" id="intro-enterprise">
          <div className="intro-v2-section-inner intro-v2-enterprise-inner">
            <div className="intro-v2-enterprise-copy" data-reveal data-reveal-delay="0">
              <p className="intro-v2-eyebrow">Enterprise</p>
              <h2 className="intro-v2-section-title intro-v2-section-title--left">
                우리 서비스만의 퍼소나,<br/>
                이제 누구나 가질 수 있습니다
              </h2>
              <p className="intro-v2-enterprise-desc">
                CRM, VOC, 인터뷰, 리서치 리포트처럼 보유중인 서비스 데이터를 연동하면
                우리 고객처럼 말하고 반응하는 AI 퍼소나가 만들어집니다.
                팀 누구나 사용자를 만나고 화면을 검증하는 데 바로 활용하세요.
              </p>
              <a
                className="intro-v2-btn intro-v2-btn--dark"
                href="/inquiry"
                target="_blank"
                rel="noopener noreferrer"
              >
                도입문의
              </a>
            </div>
            <figure className="intro-v2-enterprise-media" data-reveal data-reveal-delay="2">
              <img
                src={sectionEnterprise}
                alt="기업 데이터(CRM, VOC, 리서치 리포트, 웹·앱 분석)를 연결해 AI 퍼소나를 생성하는 흐름"
              />
            </figure>
          </div>
        </section>

        <section className="intro-v2-panel intro-v2-section intro-v2-intro" id="intro-cycle">
          <div className="intro-v2-section-inner">
            <p className="intro-v2-eyebrow" data-reveal data-reveal-delay="0">
              ReOpsAI 소개
            </p>
            <h2 className="intro-v2-section-title" data-reveal data-reveal-delay="1">
              모든 순간에서 활용하는 리서치 전문 Agent
            </h2>
            <figure className="intro-v2-media" data-reveal data-reveal-delay="2">
              <img
                src={sectionCycle}
                alt="리서치 프로세스 전 구간에서 AI 리서처, AI 퍼소나, UX 테스트 에이전트가 반복 활용되는 흐름"
              />
            </figure>
            <ul className="intro-v2-capability-list">
              {INTRO_FEATURES.map((feature) => (
                <li key={feature.id}>
                  <IconSpecial name={feature.icon} alt="" />
                  <span>{feature.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="intro-v2-panel intro-v2-security" id="intro-security">
          <div className="intro-v2-section-inner" data-reveal data-reveal-delay="0">
            <h2>Data Security</h2>
            <p>
              <span className="intro-v2-security-line">
                사용자의 데이터는 안전하게 보호됩니다. OpenAI, Google 등 신뢰할 수 있는 LLM API의 Zero-Training(비학습)
                정책을 적용하여,
              </span>
              <span className="intro-v2-security-line">
                입력하신 데이터는 AI 모델 학습에 절대 사용되지 않습니다. 분석 기록은 서비스 제공 및 이력 관리를 위해
                안전한 서버에 보관되며, 원하실 때 언제든 삭제 가능합니다.
              </span>
            </p>
          </div>
        </section>

        <footer className="intro-v2-footer">
          <div className="intro-v2-footer-inner">
            <IconSpecial name="logo" className="intro-v2-footer-logo" />
            <div className="intro-v2-footer-links">
              <Link to="/terms">이용약관</Link>
              <Link to="/privacy">개인정보/데이터처리방침</Link>
            </div>
            <p className="intro-v2-footer-copy">ReOpsAI © 2026</p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default IntroPageV2;
